import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import { IAttachment } from "../types";
import { uploadFile } from "../services/askDostService";
import { MAX_ATTACHMENTS } from "../utils/constants";

export const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

export const ALLOWED_FILE_EXTENSIONS = [".pdf", ".csv", ".xlsx"];

export const FILE_INPUT_ACCEPT = [
  ...ALLOWED_FILE_EXTENSIONS,
  ...ALLOWED_FILE_TYPES,
].join(",");

const TOO_MANY_FILES_BACKEND_MESSAGE =
  "Too many files in this conversation. Start a new conversation to attach more.";

export interface PendingUpload {
  id: string;
  file: File;
  isUploading: boolean;
  attachment?: IAttachment;
  error?: string;
}

interface IUseFileUpload {
  attachments: IAttachment[];
  onAttachmentsChange: (attachments: IAttachment[]) => void;
  translate: (text: string, options?: any) => string;
  initialPendingUploads?: PendingUpload[];
  onPendingUploadsChange?: (pendingUploads: PendingUpload[]) => void;
}

let pendingUploadIdCounter = 0;
const nextPendingUploadId = () => `upload-${++pendingUploadIdCounter}`;

const isAllowedFileType = (file: File) => {
  const name = file.name.toLowerCase();
  return (
    ALLOWED_FILE_EXTENSIONS.some((ext) => name.endsWith(ext)) ||
    ALLOWED_FILE_TYPES.includes(file.type)
  );
};

const isSameFile = (file: File, other: { name: string; size: number }) =>
  file.name === other.name && file.size === other.size;

export const useFileUpload = ({
  attachments,
  onAttachmentsChange,
  translate,
  initialPendingUploads,
  onPendingUploadsChange,
}: IUseFileUpload) => {
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>(
    initialPendingUploads ?? [],
  );
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setIsUploading] = useState(false);

  const attachmentsRef = useRef<IAttachment[]>(attachments);
  const pendingUploadsRef = useRef<PendingUpload[]>(pendingUploads);

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    onPendingUploadsChange?.(pendingUploads);
  }, [pendingUploads, onPendingUploadsChange]);

  useEffect(() => {
    setPendingUploads((prev) => {
      const knownHandles = new Set(
        prev.map((upload) => upload.attachment?.handle).filter(Boolean),
      );
      const missing = attachments.filter(
        (attachment) => !knownHandles.has(attachment.handle),
      );
      if (missing.length === 0) return prev;

      const newState = [
        ...prev,
        ...missing.map((attachment) => ({
          id: nextPendingUploadId(),
          file: new File([], attachment.name),
          isUploading: false,
          attachment,
        })),
      ];
      pendingUploadsRef.current = newState;
      return newState;
    });
  }, [attachments]);

  const uploadOne = useCallback(
    async (file: File) => {
      if (!isAllowedFileType(file)) {
        enqueueSnackbar(
          translate("invalid_file_type", { fileName: file.name }),
          { variant: "error" },
        );
        return;
      }

      const pendingId = nextPendingUploadId();

      const totalCount =
        attachmentsRef.current.length +
        pendingUploadsRef.current.filter((upload) => !upload.error).length;

      if (totalCount >= MAX_ATTACHMENTS) {
        enqueueSnackbar(translate("upload_limit_exceeded"), {
          variant: "error",
        });
        setPendingUploads((prev) => {
          const newState = [
            ...prev,
            {
              id: pendingId,
              file,
              isUploading: false,
              error: translate("upload_limit_exceeded"),
            },
          ];
          pendingUploadsRef.current = newState;
          return newState;
        });
        return;
      }

      const isDuplicate =
        attachmentsRef.current.some((attachment) =>
          isSameFile(file, attachment),
        ) ||
        pendingUploadsRef.current.some(
          (upload) => !upload.error && isSameFile(file, upload.file),
        );

      if (isDuplicate) {
        enqueueSnackbar(translate("duplicate_file", { fileName: file.name }), {
          variant: "error",
        });
        setPendingUploads((prev) => {
          const newState = [
            ...prev,
            {
              id: pendingId,
              file,
              isUploading: false,
              error: translate("duplicate_file", { fileName: file.name }),
            },
          ];
          pendingUploadsRef.current = newState;
          return newState;
        });
        return;
      }

      setPendingUploads((prev) => {
        const newState = [...prev, { id: pendingId, file, isUploading: true }];
        pendingUploadsRef.current = newState;
        return newState;
      });
      setIsUploading(true);

      try {
        const formData = new FormData();
        formData.append("file", file);

        const res = await uploadFile(formData);

        if (res?.status === 200 && res.data) {
          const attachment: IAttachment = res.data;

          const updatedAttachments = [...attachmentsRef.current, attachment];
          attachmentsRef.current = updatedAttachments;
          onAttachmentsChange(updatedAttachments);

          setPendingUploads((prev) => {
            const newState = prev.map((upload) =>
              upload.id === pendingId
                ? { ...upload, isUploading: false, attachment }
                : upload,
            );
            pendingUploadsRef.current = newState;
            return newState;
          });
        } else {
          const backendMessage =
            res?.data?.detail ||
            res?.data?.Detail ||
            res?.data?.message ||
            res?.data?.Title;
          const errorMessage =
            backendMessage === TOO_MANY_FILES_BACKEND_MESSAGE
              ? translate("too_many_files_error")
              : backendMessage
              ? backendMessage
              : translate("unable_to_upload_file", { fileName: file.name });

          enqueueSnackbar(errorMessage, { variant: "error" });

          setPendingUploads((prev) => {
            const newState = prev.map((upload) =>
              upload.id === pendingId
                ? { ...upload, isUploading: false, error: errorMessage }
                : upload,
            );
            pendingUploadsRef.current = newState;
            return newState;
          });
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        setPendingUploads((prev) => {
          const newState = prev.map((upload) =>
            upload.id === pendingId
              ? {
                  ...upload,
                  isUploading: false,
                  error: translate("unable_to_upload_file", {
                    fileName: file.name,
                  }),
                }
              : upload,
          );
          pendingUploadsRef.current = newState;
          return newState;
        });
      } finally {
        setIsUploading(false);
      }
    },
    [onAttachmentsChange, enqueueSnackbar, translate],
  );

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      for (const file of Array.from(files)) {
        await uploadOne(file);
      }
    },
    [uploadOne],
  );

  const removePendingUpload = useCallback(
    (pendingId: string) => {
      setPendingUploads((prev) => {
        const upload = prev.find((item) => item.id === pendingId);
        if (upload?.attachment) {
          const updatedAttachments = attachmentsRef.current.filter(
            (attachment) => attachment.handle !== upload.attachment!.handle,
          );
          attachmentsRef.current = updatedAttachments;
          onAttachmentsChange(updatedAttachments);
        }

        const newState = prev.filter((item) => item.id !== pendingId);
        pendingUploadsRef.current = newState;
        return newState;
      });
    },
    [onAttachmentsChange],
  );

  const resetPendingUploads = () => {
    setPendingUploads([]);
    pendingUploadsRef.current = [];
  };

  return {
    pendingUploads,
    isUploading,
    uploadFiles,
    removePendingUpload,
    resetPendingUploads,
  };
};
